import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { loanActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { formatDate, formatCurrency, addFullYears, handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { RentalUnitLeaseDialog, RentalExpenseEntryDialog } from '../common/LoanComponents';
import { RentalExpenseType, RentalExpenseTypeOptions, RentalLeaseType, RentalLeaseTypeOptions } from '../common/LoanConstants';
import { v4 as uuidv4 } from 'uuid';
import { CommonLoanSummary } from '../common/LoanComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faPlus } from '@fortawesome/free-solid-svg-icons';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { CommonLoanSummaryInvokingFeatureType } from '../common/constants/CommonLoanSummaryInvokingFeatureType';

const RentalDscrModal = () => {
    const { isOpen, propertyId } = useSelector((state) => state.loan.rentalDscrModal);
    const { dispatch } = store;

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        formColumn: { paddingBottom: '3px' },
        unitHeader: { backgroundColor: 'rgb(225 225 225)', padding: '3px 5px' },
        nameEdit: { paddingBottom: '3px', whiteSpace: 'nowrap' },
        formRow: { marginBottom: '0' },
        sectionHeader: { margin: '10px 0' },
        bfisearch: {
            width: '250px',
            position: 'absolute',
            right: '20px',
            paddingTop: '15px',
        },
        infoSection: {
            backgroundColor: '#c0c0c07a',
            display: 'inline-block',
            width: '100%',
            padding: '5px',
            marginTop: '5px',
            marginBottom: '10px',
        },
        editLease: { verticalAlign: 'unset', marginBottom: '0' },
    };

    const [loan, set_loan] = useState(null);
    const [units, set_units] = useState([]);
    const [editUnitIndex, set_editUnitIndex] = useState(null);
    const [expenses, set_expenses] = useState([]);
    const [expenseNotes, set_expenseNotes] = useState('');

    //const [id, set_id] = useState(0);
    const [lease, set_lease] = useState(null);
    const [expenseEntry, set_expenseEntry] = useState(null);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const _handleClose = () =>
        dispatch(
            loanActions.setRentalDscrModal({
                rentalDscrModal: { isOpen: false },
            })
        );

    const onCancelLease = () => {
        set_lease(null);
    };

    const onSubmitLease = (lease) => {
        const unit = units.find((u) => u.guid === lease.unitGuid);
        if (unit) {
            const idx = unit.leases.findIndex((a) => a.guid === lease.guid);
            if (idx > -1) {
                unit.leases[idx] = lease;
            } else {
                unit.leases.push(lease);
            }
            set_units([...units]);
        }
        set_lease(null);
    };

    const onCancelExpenseEntry = () => {
        set_expenseEntry(null);
    };

    const onSubmitExpenseEntry = (entry) => {
        const expense = expenses.find((u) => u.guid === entry.expenseGuid);
        if (expense) {
            const idx = expense.entries.findIndex((a) => a.guid === entry.guid);
            if (idx > -1) {
                expense.entries[idx] = entry;
            } else {
                expense.entries.push(entry);
            }
            set_expenses([...expenses]);
        }
        set_expenseEntry(null);
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                units,
                expenses,
                expenseNotes,
            };

            //Massage dates
            model.units.forEach((u) => {
                u.leases.forEach((l) => {
                    l.startDate = new Date(l.startDate).toLocaleDateString('en-us');
                    l.endDate = new Date(l.endDate).toLocaleDateString('en-us');
                });
            });

            model.expenses.forEach((u) => {
                u.entries.forEach((l) => {
                    l.yearEnd = new Date(l.yearEnd).toLocaleDateString('en-us');
                });
            });

            const response = await loanApi.setRentalDscr(propertyId, model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }
            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Loan updated successfully.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    useEffect(() => {
        async function propertyIdChanged() {
            if (propertyId && propertyId > 0) {
                await loadLoanInfo(propertyId);
            } else {
                resetState();
            }
            set_loadingOpen(false);
        }

        propertyIdChanged();
    }, [isOpen, propertyId]);

    const resetState = () => {
        //set_id(0);
        set_loan(null);
        set_units([]);
        set_expenses([]);
        set_expenseNotes('');
    };

    const loadLoanInfo = async (id) => {
        set_loadingMessage('Loading...');
        set_loadingOpen(true);

        const response = await loanApi.getRentalDscr(id);
        if (response.statusCode !== 200) {
            set_loadingOpen(false);
            return;
        }
        const loan = response.result;
        set_loan(loan);

        //Give each unit and lease a unique id
        loan.units.forEach((u) => {
            u.guid = uuidv4();
            u.leases.forEach((l) => {
                l.unitGuid = u.guid;
                l.guid = uuidv4();
            });
        });
        set_units(loan.units);

        //Give each unit and lease a unique id
        loan.expenses.forEach((u) => {
            u.guid = uuidv4();
            u.entries.forEach((l) => {
                l.expenseGuid = u.guid;
                l.guid = uuidv4();
            });
        });
        set_expenses(loan.expenses);
        set_expenseNotes(loan.expenseNotes);

        set_loadingOpen(false);
    };

    function addDays(date, days) {
        var result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

    const addLease = (index) => {
        const unit = units[index];
        if (!unit) {
            return;
        }

        let minDate = null;
        let lastAmount = null;
        if (unit.leases.length > 0) {
            minDate = addDays(unit.leases[unit.leases.length - 1].endDate, 1);
            lastAmount = unit.leases[unit.leases.length - 1].monthlyRent;
        }

        const newLease = {
            id: 0,
            unitGuid: unit.guid,
            guid: uuidv4(),
            startDate: null,
            endDate: null,
            monthlyRent: lastAmount,
            leaseTypeId: RentalLeaseType.Annual,
            notes: null,
            minDate,
        };

        set_lease(newLease);
    };

    const editLease = (unitIndex, leaseIndex) => {
        set_lease(units[unitIndex].leases[leaseIndex]);
    };

    const deleteLease = (unitIndex, leaseIndex) => {
        units[unitIndex].leases.splice(leaseIndex, 1);
        set_units([...units]);
    };

    const editUnitName = (index) => {
        set_editUnitIndex(index);
    };

    const onUnitNameChange = (index, value) => {
        const unit = units[index];
        if (!unit) {
            return;
        }
        unit.unitName = value;
        set_units([...units]);
    };

    const saveUnitName = (index) => {
        const unit = units[index];
        if (!unit) {
            return;
        }
        if (unit.unitName) {
            set_editUnitIndex(null);
        }
    };

    const addExpenseEntry = (index) => {
        const expense = expenses[index];
        if (!expense) {
            return;
        }

        let minDate = null;
        let lastAmount = null;
        if (expense.entries.length > 0) {
            minDate = addFullYears(expense.entries[expense.entries.length - 1].yearEnd, 1);
            lastAmount = expense.entries[expense.entries.length - 1].amount;
        }

        const newEntry = {
            id: 0,
            expenseGuid: expense.guid,
            guid: uuidv4(),
            amount: lastAmount,
            yearEnd: null,
            minDate,
            expenseTypeId: expense.expenseTypeId,
        };

        set_expenseEntry(newEntry);
    };

    const editExpenseEntry = (expenseIndex, entryIndex) => {
        const entry = expenses[expenseIndex].entries[entryIndex];
        entry.expenseTypeId = expenses[expenseIndex].expenseTypeId;
        set_expenseEntry(entry);
    };

    const deleteExpenseEntry = (expenseIndex, entryIndex) => {
        expenses[expenseIndex].entries.splice(entryIndex, 1);
        set_expenses([...expenses]);
    };

    if (!isOpen || !loan) {
        return '';
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '750px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1 style={{ whiteSpace: 'nowrap' }}>Debt Service Coverage Ratio</h1>
                                    <div style={styles.bfisearch}>
                                        {/* <BfiNumberSearch loanTypeId={loan?.loanTypeId} onSelect={selectBfiNumber} /> */}
                                    </div>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={_handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                            </div>

                            <div
                                className='standard-form no-search'
                                style={{ padding: '0', margin: '0' }}
                            >
                                <form
                                    name='dscrForm'
                                    onSubmit={(event) => onSubmit(event)}
                                >
                                    <div style={{ padding: '0px 7px' }}>
                                        <div
                                            style={styles.form}
                                            className={'standard-form'}
                                        >
                                            <h2
                                                className='decorated'
                                                style={styles.sectionHeader}
                                            >
                                                Leases
                                            </h2>
                                            {units &&
                                                units.map((u, unitIndex) => (
                                                    <div key={'unit_' + unitIndex}>
                                                        <div
                                                            className='row'
                                                            style={styles.formRow}
                                                        >
                                                            <div
                                                                className='columns small-12 large-9'
                                                                style={styles.unitHeader}
                                                            >
                                                                <b
                                                                    style={{
                                                                        color: '#2c5591',
                                                                    }}
                                                                >
                                                                    Unit {unitIndex + 1}
                                                                    &nbsp;&nbsp;-&nbsp;&nbsp;
                                                                    {u.unitName}
                                                                </b>
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-3'
                                                                style={styles.formColumn}
                                                            >
                                                                <button
                                                                    type='button'
                                                                    onClick={() => editUnitName(unitIndex)}
                                                                    className='button hollow'
                                                                    title='Edit Unit Name'
                                                                    style={{
                                                                        verticalAlign: 'unset',
                                                                        marginBottom: '0',
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faPencil} />
                                                                </button>
                                                                &nbsp;
                                                                <button
                                                                    type='button'
                                                                    onClick={() => addLease(unitIndex)}
                                                                    className='button hollow'
                                                                    title='Add Lease'
                                                                    style={{
                                                                        verticalAlign: 'unset',
                                                                        marginBottom: '0',
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faPlus} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {editUnitIndex === unitIndex && (
                                                            <div
                                                                className='row'
                                                                style={styles.formRow}
                                                            >
                                                                <div
                                                                    className='columns small-12 large-7'
                                                                    style={styles.formColumn}
                                                                >
                                                                    <input
                                                                        placeholder='Unit Name'
                                                                        type='text'
                                                                        id={'unitName_' + unitIndex}
                                                                        maxLength={100}
                                                                        value={u.unitName}
                                                                        onChange={(e) =>
                                                                            onUnitNameChange(unitIndex, e.target.value)
                                                                        }
                                                                        required={true}
                                                                    />
                                                                </div>
                                                                <div
                                                                    className='columns small-12 large-2'
                                                                    style={{
                                                                        padding: '10px 0px',
                                                                    }}
                                                                >
                                                                    <button
                                                                        type='button'
                                                                        onClick={() => saveUnitName(unitIndex)}
                                                                        className='button hollow'
                                                                        title='Save Unit Name'
                                                                        style={{
                                                                            verticalAlign: 'unset',
                                                                            marginBottom: '0',
                                                                        }}
                                                                    >
                                                                        Save
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {u.leases &&
                                                            u.leases.map((l, leaseIndex) => (
                                                                <div key={'lease_' + leaseIndex}>
                                                                    <div
                                                                        className='row'
                                                                        style={styles.formRow}
                                                                    >
                                                                        <div
                                                                            className='columns small-12 large-9'
                                                                            style={styles.formColumn}
                                                                        >
                                                                            {formatDate(l.startDate)} - {formatDate(l.endDate)} @
                                                                            {formatCurrency(l.monthlyRent, 0)}
                                                                            /mnth{' '}
                                                                            {
                                                                                RentalLeaseTypeOptions.find(
                                                                                    (o) => o.id === l.leaseTypeId
                                                                                ).name
                                                                            }
                                                                        </div>
                                                                        <div
                                                                            className='columns small-12 large-3'
                                                                            style={styles.formColumn}
                                                                        >
                                                                            <button
                                                                                type='button'
                                                                                onClick={() => editLease(unitIndex, leaseIndex)}
                                                                                className='button hollow'
                                                                                title='Edit Lease'
                                                                                style={styles.editLease}
                                                                            >
                                                                                <FontAwesomeIcon icon={faPencil} />
                                                                            </button>
                                                                            &nbsp;
                                                                            <button
                                                                                type='button'
                                                                                onClick={() => deleteLease(unitIndex, leaseIndex)}
                                                                                className='button hollow'
                                                                                title='Delete Lease'
                                                                                style={styles.editLease}
                                                                            >
                                                                                <FontAwesomeIcon icon={faTrashCan} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                    </div>
                                                ))}
                                            <h2
                                                className='decorated'
                                                style={styles.sectionHeader}
                                            >
                                                Annual Expenses
                                            </h2>

                                            {expenses &&
                                                expenses.map((e, expenseIndex) => (
                                                    <div key={'unit_' + expenseIndex}>
                                                        <div
                                                            className='row'
                                                            style={styles.formRow}
                                                        >
                                                            <div
                                                                className='columns small-12 large-9'
                                                                style={styles.unitHeader}
                                                            >
                                                                <b
                                                                    style={{
                                                                        color: '#2c5591',
                                                                    }}
                                                                >
                                                                    {
                                                                        RentalExpenseTypeOptions.find(
                                                                            (et) => et.id === e.expenseTypeId
                                                                        ).name
                                                                    }
                                                                </b>
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-3'
                                                                style={styles.formColumn}
                                                            >
                                                                <button
                                                                    type='button'
                                                                    onClick={() => addExpenseEntry(expenseIndex)}
                                                                    className='button hollow'
                                                                    title='Add Annual Expense'
                                                                    style={{
                                                                        verticalAlign: 'unset',
                                                                        marginBottom: '0',
                                                                    }}
                                                                    disabled={
                                                                        !RentalExpenseTypeOptions.find(
                                                                            (et) => et.id === e.expenseTypeId
                                                                        ).isAnnual && e.entries.length > 0
                                                                    }
                                                                >
                                                                    <FontAwesomeIcon icon={faPlus} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {e.entries &&
                                                            e.entries.map((l, entryIndex) => (
                                                                <div key={'lease_' + entryIndex}>
                                                                    <div
                                                                        className='row'
                                                                        style={styles.formRow}
                                                                    >
                                                                        <div
                                                                            className='columns small-12 large-9'
                                                                            style={styles.formColumn}
                                                                        >
                                                                            {(e.expenseTypeId ===
                                                                                RentalExpenseType.ManagementFees ||
                                                                                e.expenseTypeId ===
                                                                                    RentalExpenseType.VacancyAdjustment) && (
                                                                                <span>{l.amount + '%'}</span>
                                                                            )}
                                                                            {e.expenseTypeId !==
                                                                                RentalExpenseType.ManagementFees &&
                                                                                e.expenseTypeId !==
                                                                                    RentalExpenseType.VacancyAdjustment && (
                                                                                    <span>
                                                                                        {formatCurrency(l.amount, 0)} Year End{' '}
                                                                                        {formatDate(l.yearEnd)}
                                                                                    </span>
                                                                                )}
                                                                        </div>
                                                                        <div
                                                                            className='columns small-12 large-3'
                                                                            style={styles.formColumn}
                                                                        >
                                                                            <button
                                                                                type='button'
                                                                                onClick={() =>
                                                                                    editExpenseEntry(expenseIndex, entryIndex)
                                                                                }
                                                                                className='button hollow'
                                                                                title='Edit Expense'
                                                                                style={styles.editLease}
                                                                            >
                                                                                <FontAwesomeIcon icon={faPencil} />
                                                                            </button>
                                                                            &nbsp;
                                                                            <button
                                                                                type='button'
                                                                                onClick={() =>
                                                                                    deleteExpenseEntry(expenseIndex, entryIndex)
                                                                                }
                                                                                className='button hollow'
                                                                                title='Delete Lease'
                                                                                style={styles.editLease}
                                                                            >
                                                                                <FontAwesomeIcon icon={faTrashCan} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                    </div>
                                                ))}
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-12'>
                                                    <label htmlFor='notes'>Notes</label>
                                                    <textarea
                                                        value={expenseNotes}
                                                        style={{
                                                            width: '100%',
                                                        }}
                                                        rows={4}
                                                        id='notes'
                                                        onChange={(e) => set_expenseNotes(e.target.value)}
                                                        required={false}
                                                        placeholder=''
                                                        maxLength={2000}
                                                    />
                                                </div>
                                            </div>

                                            <CommonLoanSummary
                                                loan={loan}
                                                invokingFeature={CommonLoanSummaryInvokingFeatureType.RentalDscr}
                                            />
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                        >
                                            Submit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <RentalUnitLeaseDialog
                lease={lease}
                onCancel={onCancelLease}
                onSubmit={onSubmitLease}
            />
            <RentalExpenseEntryDialog
                entry={expenseEntry}
                onCancel={onCancelExpenseEntry}
                onSubmit={onSubmitExpenseEntry}
            />
        </>
    );
};

export default RentalDscrModal;
